import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import FormikProvider from '../../shared/forms/formikProvider.component';
import Modal from '../../shared/modals/modal.component';
import Form from '../../shared/forms/form.component';

import getSchema from './taxBookingEntityForm.schema';

import { PeriodPropTypes } from '../shared/propTypes';
import { OptionsPropType } from '../../shared/propTypes/common';

import {
  fetchInitialDataForTaxBookingEntityModal,
  fetchTBECompanyCodesForTaxBookingEntityModal,
} from './store/actions';

import {
  enabledFlagOptionsSelector,
  companyCodeOptionsSelector,
  tbeCompanyCodeOptionsSelector,
  importStatusOptionsSelector,
  usTaxTypeOptionsSelector,
  localTaxTypeOptionsSelector,
  tbeTypeOptionsSelector,
  isFetchingInitialDataForModalSelector,
  isFetchingTBECompanyCodesForModalSelector,
  sharedSelectors,
} from './store/selectors';
import { TaxBookingEntityPropTypes } from '../../shared/propTypes/taxBookingEntities';
import { userSelector } from '../../userData/store/selectors';
import { UserPropTypes } from '../../shared/propTypes/users';
import getPermissionsFromTree from '../../shared/authorization/getPermissionsFromTree.helper';
import permissionPaths from '../../shared/authorization/permissionPaths';
import permissionTypes from '../../shared/authorization/permissionTypes';

const ModalTitles = {
  ADD: 'Add Tax Booking Entity',
  EDIT: 'Edit Tax Booking Entity',
};

class AddTaxBookingEntityModal extends PureComponent {
  static propTypes = {
    fetchInitialDataForTaxBookingEntityModal: PropTypes.func.isRequired,
    fetchTBECompanyCodesForTaxBookingEntityModal: PropTypes.func.isRequired,
    hideModal: PropTypes.func.isRequired,
    saveEntity: PropTypes.func.isRequired,
    enabledFlagOptions: OptionsPropType,
    companyCodeOptions: OptionsPropType,
    tbeCompanyCodeOptions: OptionsPropType,
    importStatusOptions: OptionsPropType,
    usTaxTypeOptions: OptionsPropType,
    localTaxTypeOptions: OptionsPropType,
    tbeTypeOptions: OptionsPropType,
    period: PeriodPropTypes.isRequired,
    entityToEdit: TaxBookingEntityPropTypes,
    openForEdit: PropTypes.bool,
    isVisible: PropTypes.bool,
    isSavingInProgress: PropTypes.bool.isRequired,
    isFetchingInitialDataForModal: PropTypes.bool.isRequired,
    isFetchingTBECompanyCodesForModal: PropTypes.bool.isRequired,
    user: UserPropTypes,
    entities: PropTypes.arrayOf(PropTypes.shape({
      companyCode: PropTypes.string.isRequired,
      legalEntity: PropTypes.string.isRequired,
      tbeCompanyCode: PropTypes.string.isRequired,
      tbeLegalEntity: PropTypes.string.isRequired,
      tbeType: PropTypes.string.isRequired,
    })).isRequired,
  };

  state = {
    addAnother: false,
  };

  componentDidMount() {
    const {
      period,
      fetchInitialDataForTaxBookingEntityModal,
    } = this.props;

    fetchInitialDataForTaxBookingEntityModal(period);
  }

  componentDidUpdate(prevProps) {
    const { period, fetchInitialDataForTaxBookingEntityModal,
      fetchTBECompanyCodesForTaxBookingEntityModal } = this.props;
    if (prevProps.period !== period) {
      fetchInitialDataForTaxBookingEntityModal(period);
    }
    if (!prevProps.entityToEdit && this.props.entityToEdit) {
      fetchTBECompanyCodesForTaxBookingEntityModal(this.props.entityToEdit);
    }
  }

  save = (formData, { setSubmitting, resetForm }) => {
    const { period, saveEntity, openForEdit } = this.props;
    const { addAnother } = this.state;

    this.setState({ addAnother: false }, () => {
      saveEntity({
        ...formData,
        periodName: period.name,
      }, openForEdit, {
        params: {
          periodName: period.name,
          companyCode: formData.companyCode,
          legalEntity: formData.legalEntity,
          tbeLegalEntity: formData.tbeLegalEntity,
          tbeType: formData.tbeType,
        },
        callback: resetForm,
        keepOpenAfterSaving: addAnother,
      });
      setSubmitting(false);
    });
  };

  renderModal = ({ formik, schema }) => {
    const {
      openForEdit,
      companyCodeOptions,
      tbeCompanyCodeOptions,
      usTaxTypeOptions,
      localTaxTypeOptions,
      tbeTypeOptions,
      enabledFlagOptions,
      importStatusOptions,
      hideModal,
      isVisible,
      isSavingInProgress,
      isFetchingInitialDataForModal,
      isFetchingTBECompanyCodesForModal,
    } = this.props;

    const submit = () => {
      this.setState({
        addAnother: false,
      }, () => formik.handleSubmit());
    };

    const submitAndAddAnotherAction = () => {
      this.setState({
        addAnother: true,
      }, () => formik.handleSubmit());
    };

    const reset = () => {
      hideModal();
      formik.resetForm();
    };

    return (
      <Modal
        className="add-tax-booking-entity-modal"
        title={openForEdit ? ModalTitles.EDIT : ModalTitles.ADD}
        closeAction={reset} // eslint-disable-line react/jsx-no-bind
        dismissText="Cancel"
        dismissAction={reset} // eslint-disable-line react/jsx-no-bind
        submitText="Save"
        submitAction={submit} // eslint-disable-line react/jsx-no-bind
        isSubmitting={formik.isSubmitting || isSavingInProgress}
        submitAndAddAnotherAction={!openForEdit ? submitAndAddAnotherAction : null}
        submitAndAddAnotherText="Save and Add Another"
        isVisible={isVisible}
        isLoading={openForEdit ? isFetchingTBECompanyCodesForModal: isFetchingInitialDataForModal}
      >
        <Form
          formik={formik}
          schema={schema}
          openForEdit={openForEdit}
          formContext={{
            companyCodeOptions,
            tbeCompanyCodeOptions: openForEdit ? tbeCompanyCodeOptions : companyCodeOptions,
            usTaxTypeOptions,
            localTaxTypeOptions,
            tbeTypeOptions,
            enabledFlagOptions,
            importStatusOptions,
          }}
        />
      </Modal>
    );
  };

  render() {
    const { entityToEdit, user, entities, openForEdit } = this.props;

    const canUserEditRestrictedFieldsPermission = getPermissionsFromTree(
      user.permissions, permissionPaths.TAX_BOOKING_ENTITIES
    )[permissionTypes.CAN_EDIT_RESTRICTED_FIELDS];

    const schema = getSchema({ disableRestrictedField: !canUserEditRestrictedFieldsPermission });

    const formattedEntityToEdit = entityToEdit
      ? {
        ...entityToEdit,
        ownership: entityToEdit.ownership.toFixed(8),
      }
      : null;

    return (
      <FormikProvider
        schema={schema}
        entityToEdit={formattedEntityToEdit}
        onSubmit={this.save}
        render={this.renderModal}
        validationContext={{ entities, openForEdit }}
      />
    );
  }
}

const mapStateToProps = state => ({
  isSavingInProgress: sharedSelectors.savingEntitySelector(state),
  enabledFlagOptions: enabledFlagOptionsSelector(state),
  companyCodeOptions: companyCodeOptionsSelector(state),
  tbeCompanyCodeOptions: tbeCompanyCodeOptionsSelector(state),
  importStatusOptions: importStatusOptionsSelector(state),
  usTaxTypeOptions: usTaxTypeOptionsSelector(state),
  localTaxTypeOptions: localTaxTypeOptionsSelector(state),
  tbeTypeOptions: tbeTypeOptionsSelector(state),
  isFetchingInitialDataForModal: isFetchingInitialDataForModalSelector(state),
  isFetchingTBECompanyCodesForModal: isFetchingTBECompanyCodesForModalSelector(state),
  user: userSelector(state),
  entities: sharedSelectors.entitiesSelector(state),
});

export default connect(
  mapStateToProps,
  {
    fetchInitialDataForTaxBookingEntityModal,
    fetchTBECompanyCodesForTaxBookingEntityModal,
  }
)(AddTaxBookingEntityModal);
